import {
    AddIcon,
    Box,
    Button,
    ButtonIcon,
    ButtonText,
    FormGroup,
    useLatest,
    usePrevious
} from "@hosttools/frontend";
import type {Listing} from "@hosttools/frontend/models/listing";
import type React from "react";
import {memo, useCallback, useEffect, useState} from "react";
import {Controller, useController, useFieldArray, useFormContext} from "react-hook-form";

import type {Bedroom} from "../type";

import BedroomForm from "./BedroomForm";
import BedroomsItem from "./BedroomsItem";

import {Table, TableHeader, TableRow} from "@/client/components/Table";

type Props = {};

const BedroomsConfiguration: React.FC<Props> = () => {
    const {control, trigger, watch} = useFormContext<Listing>();
    const [bedroom, setBedroom] = useState<Bedroom | undefined>();
    const [indexEditing, setIndexEditing] = useState<number>();
    const latestBedroom = useLatest(bedroom);
    const propertyType = watch("propertyType");
    const propertyTypePrevious = usePrevious(propertyType);
    const {
        formState: {errors}
    } = useController<Listing, "bedroomConfiguration.bedrooms">({
        control,
        name: "bedroomConfiguration.bedrooms",
        rules: {
            required: "Room is required",
            validate: value => {
                if (latestBedroom.current) {
                    return "Please save or close form to continue";
                }
                // Allow 0 bedrooms for studio/efficiency property types (PCT46 and PCT110)
                if (value.length === 0 && propertyType !== "PCT46" && propertyType !== "PCT110") {
                    return "Please add at least one room";
                }
                if (value.some((bedroom: Bedroom) => bedroom.beds.bed.length === 0)) {
                    return "Please add at least one bed to each room";
                }
                return true;
            }
        }
    });
    const {fields, append, update, remove} = useFieldArray({
        control,
        name: "bedroomConfiguration.bedrooms"
    });

    // Re-validate bedrooms when propertyType changes
    useEffect(() => {
        // Don't trigger validation when initializing form
        if (typeof propertyTypePrevious === "undefined" || propertyType === propertyTypePrevious) {
            return;
        }
        trigger("bedroomConfiguration.bedrooms");
    }, [propertyType, trigger, propertyTypePrevious]);

    const handleAddNewBedroom = useCallback(() => {
        setBedroom({
            type: "Bedroom",
            privateBathroom: false,
            beds: {
                bed: []
            }
        });
    }, []);

    const handleChange = useCallback(
        (bedroom: Bedroom, index?: number) => {
            if (typeof index === "undefined") {
                append(bedroom);
                setBedroom(undefined);
                latestBedroom.current = undefined;
                trigger("bedroomConfiguration.bedrooms");
                return;
            }
            update(index, bedroom);
            setBedroom(undefined);
            latestBedroom.current = undefined;
            trigger("bedroomConfiguration.bedrooms");
        },
        [update, latestBedroom, trigger, append]
    );

    const handleRemove = useCallback(
        (index: number) => {
            remove(index);
        },
        [remove]
    );

    const handleEdit = useCallback(
        (bedroom: Bedroom, index: number) => {
            setIndexEditing(index);
            setBedroom(bedroom);
        },
        [setBedroom]
    );

    const handleCancel = useCallback(() => {
        setBedroom(undefined);
        latestBedroom.current = undefined;
        trigger("bedroomConfiguration.bedrooms");
    }, [setBedroom, trigger, latestBedroom]);

    return (
        <FormGroup
            error={errors.bedroomConfiguration?.bedrooms?.root?.message}
            label="Bedrooms configuration"
            name="bedroomConfiguration.bedrooms.root"
        >
            {!bedroom && (
                <Box className="flex items-end">
                    <Button testID="btn-add-new-room" variant="ghost" onPress={handleAddNewBedroom}>
                        <ButtonIcon as={AddIcon} />
                        <ButtonText>New room</ButtonText>
                    </Button>
                </Box>
            )}
            {fields.length > 0 && (
                <Table size="sm">
                    <TableRow>
                        <TableHeader>Room</TableHeader>
                        <TableHeader>Bed type</TableHeader>
                        <TableHeader>Count</TableHeader>
                        <TableHeader>Is private bathroom</TableHeader>
                        <TableHeader align="end">Action</TableHeader>
                    </TableRow>
                    {fields.map((field, index) => (
                        <Controller
                            key={field.id}
                            control={control}
                            name={`bedroomConfiguration.bedrooms.${index}`}
                            render={({field}) => (
                                <BedroomsItem
                                    bedroom={field.value}
                                    index={index}
                                    onEdit={handleEdit}
                                    onRemove={handleRemove}
                                />
                            )}
                        />
                    ))}
                </Table>
            )}
            {bedroom && (
                <BedroomForm
                    bedroom={bedroom}
                    index={indexEditing}
                    onCancel={handleCancel}
                    onChange={handleChange}
                />
            )}
        </FormGroup>
    );
};

export default memo(BedroomsConfiguration);
